import axios from 'axios';

/**
 * The licences register (migration 119) from the browser: the calls, and the
 * words every licence screen uses - so the list, a licence's page, a
 * person's, a device's and the joiner and leaver checklists say "Per
 * person", "Renews in 12 days" and "3 of 2 seats - over by 1" the same way.
 *
 * Administrators only, like the server (routes/licences.js): the screens are
 * only drawn for an administrator whose Licences switch is on, and every call
 * is refused to anybody else whatever this file does.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const licencesApi = {
  list: (params) => axios.get(`${API}/licences`, { params }).then((r) => r.data),
  summary: () => axios.get(`${API}/licences/summary`).then((r) => r.data),
  exportRows: (params) => axios.get(`${API}/licences/export`, { params }).then((r) => r.data),
  record: (params) => axios.get(`${API}/licences/record`, { params }).then((r) => r.data),
  options: () => axios.get(`${API}/licences/options`).then((r) => r.data),
  devices: (q) => axios.get(`${API}/licences/options/devices`, { params: { q } }).then((r) => r.data.devices || []),
  get: (id) => axios.get(`${API}/licences/${id}`).then((r) => r.data),
  create: (body) => axios.post(`${API}/licences`, body).then((r) => r.data),
  update: (id, body) => axios.patch(`${API}/licences/${id}`, body).then((r) => r.data),
  status: (id, status, reason) => axios.post(`${API}/licences/${id}/status`, { status, reason }).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/licences/${id}`).then((r) => r.data),
  assign: (id, body) => axios.post(`${API}/licences/${id}/seats`, body).then((r) => r.data),
  unassign: (id, seatId, reason) => axios.delete(`${API}/licences/${id}/seats/${seatId}`, { data: reason ? { reason } : {} }).then((r) => r.data),
  person: (userId) => axios.get(`${API}/licences/people/${userId}`).then((r) => r.data),
  freePerson: (userId, body) => axios.post(`${API}/licences/people/${userId}/free`, body || {}).then((r) => r.data),
  standard: (userId) => axios.get(`${API}/licences/people/${userId}/standard`).then((r) => r.data.standard || []),
  giveStandard: (userId, body) => axios.post(`${API}/licences/people/${userId}/standard`, body || {}).then((r) => r.data),
  device: (assetId) => axios.get(`${API}/licences/assets/${assetId}`).then((r) => r.data),
  upload: (id, form) => axios.post(`${API}/licences/${id}/documents`, form).then((r) => r.data),
  removeDocument: (id, docId) => axios.delete(`${API}/licences/${id}/documents/${docId}`).then((r) => r.data),
  documentPath: (id, docId) => `/licences/${id}/documents/${docId}`
};

/** The sentence a refusal carries, or `fallback`. */
export const errorOf = (e, fallback = 'That did not work.') => e?.response?.data?.error || fallback;

export const TYPE_LABEL = {
  per_user: 'Per person',
  per_device: 'Per device',
  site: 'Site or tenant',
  perpetual: 'Perpetual'
};
export const TYPE_HINT = {
  per_user: 'A seat for each person who uses it.',
  per_device: 'A seat for each device it is installed on.',
  site: 'The whole office or tenant - people and devices alike.',
  perpetual: 'Bought once and kept: no renewal.'
};
export const PERIOD_LABEL = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Yearly',
  biennial: 'Every 2 years',
  triennial: 'Every 3 years',
  one_off: 'One-off'
};
const PERIOD_PER = { monthly: 'a month', quarterly: 'a quarter', annual: 'a year', biennial: 'every 2 years', triennial: 'every 3 years', one_off: 'once' };
export const STATUS_LABEL = { active: 'Active', cancelled: 'Cancelled', expired: 'Expired' };
export const statusTone = (s) => ({ active: 'success', cancelled: 'idle', expired: 'critical' }[s] || 'idle');
export const DOC_LABEL = { contract: 'Contract', invoice: 'Invoice', quote: 'Quote', other: 'Other' };

/** An amount in its currency: €1,234.56, or 1,234.56 USD. */
export function amount(n, currency = 'EUR', { round = false } = {}) {
  const v = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(v)) return '—';
  const text = v.toLocaleString('en-IE', { minimumFractionDigits: round ? 0 : 2, maximumFractionDigits: round ? 0 : 2 });
  return currency === 'EUR' ? `€${text}` : `${text} ${currency}`;
}

/** "€12.50 a month", "€1,200.00 once", or null. */
export const costWords = (l) => (l.cost === null || l.cost === undefined ? null
  : `${amount(l.cost, l.currency)} ${PERIOD_PER[l.billing_period] || ''}`.trim());

/** A list of { currency, amount } as one line: "€1,234 + 300 USD". */
export const totals = (list, opts) => ((list || []).length
  ? list.map((t) => amount(t.amount, t.currency, opts)).join(' + ') : amount(0, 'EUR', opts));

/** "12 March 2027" from 'YYYY-MM-DD', in the firm's English. */
export function dayWords(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
export const shortDay = (day) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * The licence's next date as a chip: { text, tone } - "Renews in 12 days",
 * "Ends today", "Renewal date passed 3 days ago", "Renews 1 May 2027".
 * Null when there is no date (or it is not active).
 */
export function nextWords(l) {
  if (!l || l.status !== 'active' || !l.next_on) return null;
  const d = Number(l.days_left);
  const verb = l.next_kind === 'end' ? 'Ends' : (l.auto_renew ? 'Renews' : 'Due for renewal');
  if (d < 0) {
    return { text: `${l.next_kind === 'end' ? 'Ended' : 'Renewal date passed'} ${-d} day${d === -1 ? '' : 's'} ago`, tone: 'critical' };
  }
  if (d === 0) return { text: `${verb} today`, tone: 'critical' };
  if (d <= 7) return { text: `${verb} in ${d} day${d === 1 ? '' : 's'}`, tone: 'critical' };
  if (d <= 30) return { text: `${verb} in ${d} days`, tone: 'warning' };
  if (d <= 90) return { text: `${verb} in ${d} days`, tone: 'info' };
  return { text: `${verb} ${shortDay(l.next_on)}`, tone: 'idle' };
}

/** "3 of 5 seats", "4 seats, no limit", with the warning when over. */
export function seatWords(l) {
  const used = Number(l.seats_used) || 0;
  if (l.seats === null || l.seats === undefined) return { text: `${used} in use, no limit`, tone: 'idle', over: false };
  const over = used > l.seats;
  if (over) return { text: `${used} of ${l.seats} seats - over by ${used - l.seats}`, tone: 'critical', over: true };
  if (used === l.seats) return { text: `${used} of ${l.seats} seats - none free`, tone: 'warning', over: false };
  return { text: `${used} of ${l.seats} seats`, tone: 'success', over: false };
}

/** The share of the bought seats in use, 0..100 (over is capped), for the bar. */
export const seatShare = (l) => (l.seats ? Math.min(100, Math.round(((Number(l.seats_used) || 0) / l.seats) * 100)) : 0);

/** Who or what holds a seat, as a line. */
export const holderOf = (s) => (s.user_id ? (s.user_name || 'Somebody')
  : [s.asset_tag, [s.manufacturer, s.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'A device');

/* ------------------------------------------- notice periods (130, wave 7) */

/** The term date of a licence or a contract as the form holds it: the renewal or the end, whichever comes first. */
export function termOn({ renewal_date: r, end_date: e, auto_renew: auto }) {
  if (r && e) return r < e ? { kind: 'renewal', on: r } : e < r ? { kind: 'end', on: e } : { kind: auto ? 'renewal' : 'end', on: e };
  if (r) return { kind: 'renewal', on: r };
  if (e) return { kind: 'end', on: e };
  return null;
}

/** 'YYYY-MM-DD' moved by whole months, the day clamped to the month's end - the server's addMonths. */
export function addMonthsTo(day, months) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return null;
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) + months;
  const y = Math.floor(total / 12);
  const mo = total % 12;
  const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  return `${y}-${String(mo + 1).padStart(2, '0')}-${String(Math.min(Number(m[3]), last)).padStart(2, '0')}`;
}

/** The deadline the form's period would give, for the hint under it (the server works it out again). */
export function deadlineFor(form) {
  const n = Number(form.notice_amount);
  const term = termOn(form);
  if (!term || form.notice_amount === '' || form.notice_amount === null || !Number.isInteger(n) || n <= 0) return null;
  if (form.notice_unit === 'months') return { ...term, deadline: addMonthsTo(term.on, -n) };
  const d = new Date(`${term.on}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return { ...term, deadline: d.toISOString().slice(0, 10) };
}

/** "Give notice by 30 Nov 2026" as a chip: { text, tone } - or null with no deadline, or one long past. */
export function noticeChip(r) {
  if (!r || !r.notice_deadline || (r.status && r.status !== 'active')) return null;
  const d = Number(r.notice_days_left);
  if (d < 0) return { text: `Notice deadline passed ${shortDay(r.notice_deadline)}`, tone: 'idle' };
  if (d === 0) return { text: 'Give notice today', tone: 'critical' };
  const tone = d <= 7 ? 'critical' : d <= (r.notice_alert ?? 30) ? 'warning' : 'idle';
  return { text: `Give notice by ${shortDay(r.notice_deadline)}`, tone };
}

/* ------------------------------------------------------- attachments */

/** The files either register keeps (lib/notice-alerts.js FILE_TYPES): the file picker's `accept`. */
export const ATTACH_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp,.heic,.eml,.msg';
export const ATTACH_WORDS = 'PDF, Word, Excel or CSV, an image, or an email (.eml, .msg)';
const ATTACH_EXT = new Set(ATTACH_ACCEPT.split(',').map((x) => x.slice(1)));

/** Why a chosen file would be refused, or null - so the form says so before the server does (it checks again: 415). */
export function attachProblem(file) {
  if (!file) return null;
  const ext = (/\.([a-z0-9]{1,8})$/i.exec(file.name || '')?.[1] || '').toLowerCase();
  if (!ATTACH_EXT.has(ext)) return `${file.name} cannot be attached: only ${ATTACH_WORDS}.`;
  if (file.size > 10 * 1024 * 1024) return `${file.name} is larger than 10 MB.`;
  return null;
}
